import { ModalContainer } from '@components'
import { FloatingPortal } from '@floating-ui/react'
import { AnimatePresence } from 'framer-motion'
import React, { useState } from 'react'
import { useModalEscape } from '../Modal/modalStack'
import { ModalDialog as StyledModalDialog, ModalOverlay } from './ModalV2.styled'

export interface ModalProps {
  header?: React.ReactNode
  headerIcon?: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
  isVisible: boolean
  width?: number
  height?: number
  // Shrink the modal to its content (capped by height) instead of filling the viewport.
  autoHeight?: boolean
  // No scale/slide. Use for tall dialogs whose entrance otherwise jumps after render.
  calm?: boolean
  handleOnClose: () => void
  noContainer?: boolean
  /** On narrow screens: title + close on row 1, headerIcon full-width on row 2. */
  stackHeaderOnMobile?: boolean
}

export const ModalV2: React.FC<ModalProps> = ({
  header,
  headerIcon,
  footer,
  isVisible,
  handleOnClose,
  width,
  height,
  autoHeight = false,
  calm = false,
  children,
  noContainer = false,
  stackHeaderOnMobile = false
}) => {
  // NOTE: Mouse up triggers also onClick event. Accidental mouseUp event on modal backdrop
  // might occur when selecting text inside modal, for example. This logic makes sure that
  // onClick events have been started on the backdrop as well.
  const [, setMouseDown] = useState(false)
  useModalEscape(isVisible, handleOnClose)

  /**
   * TODO: Need to add <FloatingFocusManager> to return the focus back to the element
   * that opened the modal with correct ref.
   * Currently, when the modal is closed the focus dissapears.
   * */

  return (
    <FloatingPortal>
      <AnimatePresence>
        {isVisible && (
          <ModalOverlay
            onMouseDown={() => setMouseDown(true)}
            onTouchStart={() => setMouseDown(true)}
            onClick={() =>
              setMouseDown(value => {
                value && handleOnClose()
                return false
              })
            }
          >
            <StyledModalDialog
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              width={width}
              height={height}
              $autoHeight={autoHeight}
              $calm={calm}
            >
              {noContainer ? (
                children
              ) : (
                <ModalContainer
                  header={header}
                  headerIcon={headerIcon}
                  footer={footer}
                  onClose={() => handleOnClose()}
                  stackHeaderOnMobile={stackHeaderOnMobile}
                >
                  {children}
                </ModalContainer>
              )}
            </StyledModalDialog>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </FloatingPortal>
  )
}
